import teamTemplatesConfig from '@/configs/permissions/templates.json';
import { BUSINESS_TYPE_IDS } from '@/lib/business-types/registry';
import type { TeamRole } from '@/lib/team/permissions';
import {
  isCapabilityApproval,
  type CapabilityApproval,
  type CapabilityDefinition,
} from './capabilities';
import { isAssignableRole } from './permissions';
import { getEffectiveCapabilities } from './workspace-capabilities';

// System team templates (doc 10 §5.1, doc 11 T-1.2). Menuraq-authored starting
// policies for common jobs, stored as versioned config — never hardcoded in
// components. Templates are filtered by active business type
// (`branchEligibility`), and a template can only grant capabilities that exist
// in the capability registry (T-1.1) and are available on the branch.
//
// This module deliberately lives in `team-templates.ts`, NOT `templates.ts`:
// `templates.ts` is the branch visual-template resolver (classic/modern/
// premium) consumed by the customer branch-environment path. Do not merge the
// two.

export type TemplateCapabilityEffect = 'allow' | 'deny';

export interface TemplateCapabilityGrant {
  key: string;
  effect: TemplateCapabilityEffect;
  approval: CapabilityApproval;
}

export interface SystemTemplate {
  code: string;
  name: string;
  description: string;
  intendedJob: string;
  /**
   * Legacy fallback role for invites built from this template (T-4.2). The
   * real access comes from the frozen policy version; this role is only what
   * the invite's legacy `role` column carries so pre-policy accept paths and
   * the compatibility projection keep working. Must be an invitable role —
   * never `service_console_manager` (enforced by `validateTemplateRegistry`).
   */
  defaultRole: TeamRole;
  schemaVersion: number;
  version: number;
  branchEligibility: string[];
  capabilities: TemplateCapabilityGrant[];
  constraints: {
    allowedWorkspaces?: ('business' | 'branch')[];
  };
}

interface TemplatesConfig {
  schemaVersion: number;
  templates: SystemTemplate[];
}

const config = teamTemplatesConfig as unknown as TemplatesConfig;

const APPROVAL_ORDER: Record<CapabilityApproval, number> = { none: 0, member: 1, manager: 2, owner: 3 };

export function getSystemTemplates(): SystemTemplate[] {
  return config.templates.map((t) => ({ ...t, capabilities: [...t.capabilities] }));
}

export function getSystemTemplate(code: string): SystemTemplate | undefined {
  return config.templates.find((t) => t.code === code);
}

export function isTemplateCode(input: unknown): input is string {
  return typeof input === 'string' && config.templates.some((t) => t.code === input);
}

/**
 * The legacy fallback role an invite built from this template carries (T-4.2).
 * The invite's `role` column is only the compatibility projection — the real
 * access comes from the frozen policy version — but accept paths that predate
 * policy refs still build from it, so it must be a valid invitable role.
 *
 * Throws on an unknown template code (routes validate with `isTemplateCode`
 * first and return a stable 400; this is the defensive server-side gate).
 */
export function getTemplateDefaultRole(code: string): TeamRole {
  const template = getSystemTemplate(code);
  if (!template) {
    throw new Error(`Unknown template code: ${code}`);
  }
  return template.defaultRole;
}

/**
 * The template picker filter (doc 10 §5.1): only templates eligible for the
 * branch's active business type are offered. Off business types are not live,
 * so no template is offered for them.
 */
export function getTemplatesForBusinessType(typeId: string): SystemTemplate[] {
  return getSystemTemplates().filter((t) => t.branchEligibility.includes(typeId));
}

export function getTemplateCapabilityKeys(template: SystemTemplate): string[] {
  return template.capabilities.map((c) => c.key);
}

/** Workspaces the template's grants actually touch (union of capability workspaces). */
export function getTemplateWorkspaces(
  template: SystemTemplate,
  capabilities: readonly CapabilityDefinition[] = getEffectiveCapabilities(),
): string[] {
  const byKey = new Map(capabilities.map((c) => [c.key, c]));
  const workspaces = new Set<string>();
  for (const grant of template.capabilities) {
    const capability = byKey.get(grant.key);
    if (capability) workspaces.add(capability.workspace);
  }
  return [...workspaces];
}

export type TemplateRegistryValidation = {
  ok: boolean;
  errors: string[];
};

/**
 * Validates the template registry against the T-1.2 rules:
 * - unique, snake_case template codes; schemaVersion 1; version >= 1
 * - defaultRole is a valid invitable role (never `service_console_manager`)
 * - every grant key exists in the capability registry
 * - no duplicate grant keys within a template
 * - effect is allow|deny; approval is from the closed enum
 * - a template cannot lower a capability's approval below its registry level
 *   (ordering none < member < manager < owner)
 * - no retired capability may be granted by a template
 * - no grant may require owner-level approval (owner-only actions are never
 *   template-grantable; doc 10 §5.3)
 * - branchEligibility only contains live business types
 * - every template grants at least one capability
 * - constraints.allowedWorkspaces covers every workspace the grants touch
 *
 * `capabilities` and `templates` are injectable so tests can exercise each
 * failure branch with a crafted config; they default to the real registries.
 */
export function validateTemplateRegistry(
  liveBusinessTypeIds: readonly string[] = BUSINESS_TYPE_IDS,
  capabilities: readonly CapabilityDefinition[] = getEffectiveCapabilities(),
  templates: readonly SystemTemplate[] = config.templates,
): TemplateRegistryValidation {
  const errors: string[] = [];
  const seenCodes = new Set<string>();
  const byKey = new Map(capabilities.map((c) => [c.key, c]));

  for (const template of templates) {
    if (seenCodes.has(template.code)) {
      errors.push(`Duplicate template code: ${template.code}`);
    }
    seenCodes.add(template.code);

    if (!/^[a-z0-9_]+$/.test(template.code)) {
      errors.push(`Template code must be snake_case: ${template.code}`);
    }

    if (template.schemaVersion !== 1) {
      errors.push(`Template ${template.code} must use schemaVersion 1`);
    }

    if (typeof template.version !== 'number' || template.version < 1) {
      errors.push(`Template ${template.code} must have version >= 1`);
    }

    // T-4.2: the legacy fallback role must be a real, invitable role. Unknown
    // roles and retired roles (service_console_manager) are rejected — an
    // invite built from this template must never carry a role the accept path
    // cannot assign.
    if (typeof template.defaultRole !== 'string' || !isAssignableRole(template.defaultRole)) {
      errors.push(`Template ${template.code} has invalid defaultRole: ${String(template.defaultRole)}`);
    }

    if (!Array.isArray(template.capabilities) || template.capabilities.length === 0) {
      errors.push(`Template ${template.code} must grant at least one capability`);
    }

    const seenGrants = new Set<string>();
    for (const grant of template.capabilities ?? []) {
      if (seenGrants.has(grant.key)) {
        errors.push(`Template ${template.code} grants ${grant.key} more than once`);
      }
      seenGrants.add(grant.key);

      // Authoritative list, same reason as `validateGrant`: a caller passing a
      // narrower registry must have that narrowing respected.
      if (!byKey.has(grant.key)) {
        errors.push(`Template ${template.code} references unknown capability ${grant.key}`);
        continue;
      }

      if (grant.effect !== 'allow' && grant.effect !== 'deny') {
        errors.push(`Template ${template.code} has invalid effect for ${grant.key}: ${String(grant.effect)}`);
      }

      if (!isCapabilityApproval(grant.approval)) {
        errors.push(`Template ${template.code} has invalid approval for ${grant.key}: ${String(grant.approval)}`);
        continue;
      }

      if (grant.approval === 'owner') {
        errors.push(`Template ${template.code} grants owner-only approval for ${grant.key}`);
      }

      const capability = byKey.get(grant.key)!;
      if (capability.retired) {
        errors.push(`Template ${template.code} grants retired capability ${grant.key}`);
      }

      if (APPROVAL_ORDER[grant.approval] < APPROVAL_ORDER[capability.approval]) {
        errors.push(
          `Template ${template.code} lowers approval for ${grant.key} below the registry level ` +
            `(${grant.approval} < ${capability.approval})`,
        );
      }
    }

    for (const typeId of template.branchEligibility ?? []) {
      if (!liveBusinessTypeIds.includes(typeId)) {
        errors.push(`Template ${template.code} is eligible for unsupported business type ${typeId}`);
      }
    }

    const touchedWorkspaces = getTemplateWorkspaces(template, capabilities);
    const allowed = new Set(template.constraints?.allowedWorkspaces ?? []);
    for (const workspace of touchedWorkspaces) {
      if (!allowed.has(workspace as 'business' | 'branch')) {
        errors.push(`Template ${template.code} grants ${workspace}-workspace capabilities but allowedWorkspaces omits ${workspace}`);
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

export { config as teamTemplatesConfig };
export { APPROVAL_ORDER };